import { useMemo } from 'react'
import { useStore } from 'react-redux'
import { useLDClient } from '@afterpaytouch/feature'
import type { CheckoutAppState } from '../../state'
import { api } from '../../env'
import { Features } from '../../model/features'
import { USER_JOURNEY_NAMES } from './constants'
import { datadogUserJourneySink, UserJourneySink } from './sink'

/**
 * The two slice paths this sink needs, read structurally instead of through the state
 * hooks (`state/selectors`' tokenSelector, `useReadyFeatureFlag`): those modules
 * transitively pull the whole RTK-Query hooks graph (checkout/consumer service
 * endpoints) into every importer of UserJourneyProvider, which breaks unrelated tests
 * that partially mock `@afterpaytouch/portal-api` service modules — the hooks
 * destructure service exports at module scope. Mirrors tokenSelector's
 * `initial?.token ?? ''` semantics (state/checkout/hooks.ts) and
 * useConsumerFeatureFlagReady's `hasFeatureFlagsReady ?? false`
 * (state/consumer/hooks.ts). Each field indexes the real state type as a compile-time
 * canary — the type-only import is erased at build (it cannot reintroduce the runtime
 * edge), and tsc fails here if either path is ever renamed or moved.
 */
type FederationGateState = {
  checkout?: { initial?: { token?: CheckoutAppState['checkout']['initial']['token'] } }
  consumer?: { hasFeatureFlagsReady?: CheckoutAppState['consumer']['hasFeatureFlagsReady'] }
}

export const recordUserJourneyUrl = (baseUrl: string, token: string): string =>
  `${baseUrl.replace(/\/+$/, '')}/portal/consumers/checkout/v2/${encodeURIComponent(token)}/user-journey`

/**
 * Path A federation (CASHDAP-3401): decorates a sink so completed journeys are also
 * POSTed to portal-api, which forwards them into the Cash User Journeys service. The
 * payload is the wire contract already — RecordCompletedJourneyPayload mirrors the Cash
 * proto field-for-field, and portal-api's RecordUserJourneyRequest accepts exactly this
 * JSON shape.
 *
 * Started events are not federated: the Cash contract is completion-only (started
 * events exist purely for Datadog's analytical abandonment derivation).
 *
 * Delivery is a best-effort beacon. Completions fire synchronously before unload
 * (pagehide anchor, merchant redirect), so the POST uses `fetch` with `keepalive` —
 * `sendBeacon` cannot carry the Content-Type the endpoint requires, and the endpoint
 * is PERMIT_ALL so no auth header is needed. Browsers without keepalive support just
 * degrade to a request that may be dropped on unload. Federation must never affect the
 * base sink or the app: the base sink reports first, and every failure here — including
 * a rejected fetch — is swallowed. Datadog stays the source of truth; the server side
 * is fire-and-forget too, so lost beacons only thin the federated copy.
 *
 * `getRecordUrl` is consulted per completion so flag flips and the checkout token are
 * evaluated live; returning undefined (flag off, no token yet) skips federation for
 * that completion only. Only journey names the portal-api endpoint allowlists are sent
 * — anything else would be dropped server-side as `unsupported_journey_name`.
 */
export const createFederatedUserJourneySink = (base: UserJourneySink, getRecordUrl: () => string | undefined): UserJourneySink => ({
  journeyStarted: (event) => {
    base.journeyStarted(event)
  },
  journeyCompleted: (payload) => {
    base.journeyCompleted(payload)
    try {
      if (payload.journey_name !== USER_JOURNEY_NAMES.CHECKOUT) {
        return
      }
      const url = getRecordUrl()
      if (url === undefined) {
        return
      }
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {
        // best-effort beacon — a lost completion is acceptable, a surfaced error is not
      })
    } catch {
      // federation must never break the base sink's reporting or the caller
    }
  },
})

/**
 * The app's default sink: Datadog, plus flag-gated federation to the Cash UJ service.
 * Must be called under the Redux Provider. The sink identity is stable across renders;
 * the federation gate and checkout token are read at completion time straight from the
 * store and the LD client, so a journey completing right before unload federates with
 * the latest state, and a mid-session kill-switch flip takes effect immediately.
 *
 * The flag is gated on `hasFeatureFlagsReady` with the same semantics as
 * `useReadyFeatureFlag` (state/featureFlags/hooks.ts): LaunchDarkly bootstraps from
 * localStorage, so before `CheckoutLaunchDarklyContext` has identified the current
 * consumer, `variation()` would serve the *previous* user's value — and evaluating it
 * would record telemetry against that stale context. Until readiness, the gate is
 * treated as off (the default) and the flag is not evaluated at all, so pre-identify
 * completions never federate.
 */
export const useFederatedUserJourneySink = (base: UserJourneySink = datadogUserJourneySink): UserJourneySink => {
  const client = useLDClient()
  const store = useStore<FederationGateState>()

  return useMemo(
    () =>
      createFederatedUserJourneySink(base, () => {
        try {
          const state = store.getState()
          if (state.consumer?.hasFeatureFlagsReady !== true) {
            return undefined
          }
          if (client?.variation(Features.CASH_USER_JOURNEY_FEDERATION_ENABLED, false) !== true) {
            return undefined
          }
          const token = state.checkout?.initial?.token ?? ''
          return token === '' ? undefined : recordUserJourneyUrl(api.PORTAL_API_BASE_URL, token)
        } catch {
          // gate or URL construction failed — skip this completion
          return undefined
        }
      }),
    [base, client, store]
  )
}
